/**
 * Top-level error boundary. A live WebGL chart plus a lot of state means a stray
 * uncaught render error would otherwise blank the whole app (white screen). This
 * catches it and shows a calm "reload to carry on" panel instead, so the user has
 * a way back rather than a dead screen. A full reload re-inits the map, the WebGL
 * context and every store from the bundle; saved data (routes, marks, settings)
 * lives in localStorage and survives the reload.
 *
 * We deliberately do NOT auto-reload: if an error is persistent, an auto-reload
 * would loop. The user taps Reload when ready.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
     
    console.error('[ErrorBoundary] uncaught render error', error, info.componentStack);
  }

  private reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: 24,
          textAlign: 'center',
          background: '#0e1620',
          color: '#e7edf3',
          fontFamily: 'system-ui, sans-serif',
          zIndex: 100000,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700 }}>The chart hit a problem</div>
        <div style={{ maxWidth: 360, lineHeight: 1.4, opacity: 0.85 }}>
          Something went wrong on screen. Reload to carry on — your routes, marks and
          settings are saved on this device and won&apos;t be lost.
        </div>
        <button
          type="button"
          onClick={this.reload}
          style={{
            marginTop: 6,
            padding: '10px 22px',
            fontSize: 16,
            fontWeight: 600,
            color: '#0e1620',
            background: '#5ec6ff',
            border: 'none',
            borderRadius: 8,
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
